/**
 * «Лог звонка» карточки разговора (`?ctab=pbx`): что видела АТС voip-2 — транк и номер, гудки, ответ сети,
 * «занято» / «не ответили», кто первым положил трубку; ниже — строки лога Asterisk этого звонка.
 * Снимает сессия (`voice-lab-pbx`, после каждого звонка — `voice-lab-call`). Спека
 * `docs/specs/2026-10-27-voice-deal-call-design.md`.
 */
import type { LabPbx } from "@/lib/voiceLab";

const ЦВЕТ: Record<string, string> = {
  answered: "text-ok",
  no_answer: "text-warn",
  busy: "text-warn",
  congestion: "text-err",
  cancel: "text-fg-muted",
  missing: "text-err",
};

function Пара({ k, v }: { k: string; v: string | number | null | undefined }) {
  if (v == null || v === "") return null;
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 text-fg-muted">{k}</dt>
      <dd className="tabular-nums">{v}</dd>
    </div>
  );
}

export function PbxTab({ pbx, room, phone }: { pbx: LabPbx | null | undefined; room: string; phone: string | null }) {
  if (!phone) {
    return <div className="text-[12.5px] text-fg-muted">Разговор в браузере — на АТС его не было.</div>;
  }
  if (!pbx) {
    return (
      <div className="flex flex-col gap-1 text-[12.5px] text-fg-muted" data-annot="лог АТС не снят">
        <span>Лог АТС ещё не снят — его снимает сессия после звонка по SSH:</span>
        <code className="w-fit rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1 font-mono text-[11.5px] text-fg">
          .venv/bin/python -m scripts.prokat_runner voice-lab-pbx --room {room}
        </code>
      </div>
    );
  }
  return (
    <section className="flex flex-col gap-3 text-[12.5px]" data-annot="лог звонка на АТС">
      <div className="text-[14px]">
        АТС: <span className={`font-semibold ${ЦВЕТ[pbx.outcome] ?? ""}`}>{pbx.outcome_label}</span>
      </div>
      <dl className="flex flex-col gap-0.5">
        <Пара k="Набран номер" v={pbx.phone} />
        <Пара k="Звонили с номера" v={pbx.caller} />
        <Пара k="До ответа" v={pbx.ring_s != null ? `${pbx.ring_s} с` : null} />
        <Пара k="Разговор на АТС" v={pbx.talk_s != null ? `${pbx.talk_s} с` : null} />
        <Пара k="Первым положил" v={pbx.hung_up_by} />
        <Пара k="Транк" v={pbx.trunk} />
        <Пара k="Звонок на АТС" v={pbx.call_id} />
        <Пара k="Снято" v={pbx.fetched.replace("T", " ")} />
      </dl>
      {!!pbx.events.length && (
        <ol className="flex flex-col gap-0.5" data-annot="события АТС">
          {pbx.events.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span className="w-16 shrink-0 tabular-nums text-fg-muted">{e.at}</span>
              <span className={ЦВЕТ[e.kind] ?? ""}>{e.what}</span>
            </li>
          ))}
        </ol>
      )}
      {!!pbx.raw?.length && (
        <details>
          <summary className="cursor-pointer text-fg-muted">Строки лога Asterisk · {pbx.raw.length}</summary>
          <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2 font-mono text-[11px]">
            {pbx.raw.join("\n")}
          </pre>
        </details>
      )}
      <div className="text-[12px] text-fg-muted">
        Записи разговора на АТС нет: исходящие робота (транк 7001) АТС не пишет.
      </div>
    </section>
  );
}
